/**
 * SEO HEADING STRUCTURE - Korean Homepage (IndexKr)
 * =================================================
 * 
 * TITLE: CSW - 온라인 자습실 | 무료 가상 스터디룸 | Online Workspace...
 * META DESCRIPTION: 무료 온라인 자습실에서 집중력과 공부 습관을 높여보세요...
 * 
 * H1 (3 tags - intentional for keyword targeting, same as English homepage):
 *   - "생산성 도구" (Productivity Tool) (Hero)
 *   - "온라인 자습실" (Online Workspace) (Hero)
 *   - "스터디 스트림" (Study Stream) (StudyStream section)
 * 
 * H2 tags follow Korean translations of English homepage sections
 */

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Video, Clock, CheckCircle2, Users, Calendar, Trophy, BookOpen, Heart, MessageCircle, Play, TrendingUp, BarChart3, Zap, Timer, Youtube, User, LayoutGrid, HelpCircle, Layers, Bell, Check, Infinity } from "lucide-react";
import { Link } from "react-router-dom";
const IndexKr = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="CSW - 온라인 자습실 | 무료 가상 스터디룸 | Online Workspace | Productivity Tool | Study Stream"
        description="무료 온라인 자습실에서 집중력과 공부 습관을 높여보세요. 24시간 이용 가능, 포모도로 타이머, 전 세계 학생들과 함께 공부하세요."
        keywords="온라인 자습실, 가상 공부방, 온라인 공부, 스터디 스트림, 포모도로 타이머"
        canonicalUrl="/kr/"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
              {/* Left Content */}
              <div className="space-y-8 animate-fade-in">
                <div className="space-y-4">
                  <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground leading-tight">
                    온라인 자습실
                  </h1>
                  <p className="text-2xl sm:text-3xl text-muted-foreground font-light">
                    집중력과 공부 습관을 높여주는 공간
                  </p>
                </div>

                <p className="text-lg text-muted-foreground leading-relaxed">
                  온라인 자습실, 특히 CSW(Chill Study Work)는 제 공부 방법을 크게 바꾸어 준 도구입니다. 집에서는 집중하기 어렵고 시간 관리도 힘들었지만, 온라인 자습실의 기능과 규칙 덕분에 학습 효율이 눈에 띄게 향상되었습니다.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    asChild
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 transition-all hover:scale-105"
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Join A Room Now
                    </a>
                  </Button>
                  <Button 
                    asChild
                    variant="outline"
                    size="lg"
                    className="font-semibold px-8 group"
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                      <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                      체험해보기
                    </a>
                  </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-8 pt-4">
                  <div>
                    <div className="text-4xl font-bold text-foreground">100k+</div>
                    <div className="text-muted-foreground mt-1">Hours Completed</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-foreground">8k+</div>
                    <div className="text-muted-foreground mt-1">Task Completed</div>
                  </div>
                </div>
              </div>

              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-border/50 bg-card/50 backdrop-blur-sm">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 opacity-50" />
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full h-auto relative z-10"
                  >
                    <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                  </video>
                </div>
                {/* Floating Stats Cards */}
                <div className="absolute -bottom-6 -left-6 bg-card/90 backdrop-blur-sm border-2 border-border/50 rounded-xl p-4 shadow-lg animate-fade-in" style={{ animationDelay: "0.4s" }}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-primary-foreground" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">생산성 향상</div>
                      <div className="text-xl font-bold text-foreground">+40%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Modes Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
                <span className="text-sm font-semibold text-primary uppercase tracking-wider">새로운 사람들을 만나세요</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                소통의 부담 없이
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                대화의 압박 없이 자연스럽게 연결될 수 있습니다.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Chill Mode */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Heart className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Chill</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  편안한 분위기에서 여유롭게 학습하세요.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    배경 음악
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    자유로운 채팅
                  </li>
                </ul>
              </Card>

              {/* Study Mode */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 border-primary hover:border-primary transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Study</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  집중된 학습 환경을 경험하세요.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    뽀모도로 타이머
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    학습 추적
                  </li>
                </ul>
              </Card>

              {/* Work Mode */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-purple-500/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-600/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Zap className="w-8 h-8 text-purple-500" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Work</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  최대 생산성을 위한 완벽한 공간.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" />
                    작업 관리
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" />
                    성과 추적
                  </li>
                </ul>
              </Card>
            </div>

            <div className="text-center mt-12">
              <Button 
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8"
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  LET'S GO!
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                온라인 자습실의 기본 특징
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                CSW는 공부 효율을 높이는 편안한 환경을 제공합니다. 학습 진행 상황을 기록하고 되돌아볼 수 있는 편리한 기능과 도구들을 제공합니다.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Feature 1 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">학습 기록과 복습</h3>
                <p className="text-muted-foreground leading-relaxed">
                  공부 시작과 종료 시간을 기록해 학습 시간을 시각적으로 확인할 수 있습니다. 이를 통해 본인의 학습 속도와 집중도를 쉽게 파악할 수 있습니다.
                </p>
              </Card>

              {/* Feature 2 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <MessageCircle className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">자유로운 자습 공간과 전용 채팅</h3>
                <p className="text-muted-foreground leading-relaxed">
                  누구나 자유롭게 이용 가능한 자습 공간에서 리액션과 채팅을 통해 다른 이용자들과 소통할 수 있습니다. 이러한 소통은 학습 동기를 유지하는 데 큰 도움이 됩니다.
                </p>
              </Card>

              {/* Feature 3 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Trophy className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">동기 부여 시스템</h3>
                <p className="text-muted-foreground leading-relaxed">
                  공부한 시간이나 달성한 과제에 따라 보상을 받을 수 있어 흥미롭게 학습을 진행할 수 있습니다. 또한, 집중력을 유지할 수 있도록 다양한 프로그램이 제공됩니다.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Alone but Never Lonely Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/5 to-background" />
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto items-center">
              <Card className="p-12 bg-card/50 backdrop-blur-sm border-2 hover:border-primary/50 transition-all">
                <h2 className="text-4xl font-bold text-foreground mb-4">혼자지만, 외롭지 않아요</h2>
                <p className="text-xl text-muted-foreground">
                  내성적인 분들에게도 특히 적합한 환경으로, 조용히 공부하며 자연스럽게 사람들과 연결될 수 있습니다.
                </p>
              </Card>

              <Card className="p-12 bg-card/50 backdrop-blur-sm border-2 hover:border-primary/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <Heart className="w-12 h-12 text-primary" />
                  <h2 className="text-4xl font-bold text-foreground">조용한 시간</h2>
                </div>
                <p className="text-xl text-muted-foreground">
                  집중이 필요한 순간에 조용한 환경을 제공합니다.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
                <span className="text-sm font-semibold text-primary uppercase tracking-wider">효과적인 학습</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                학습 습관과 동기 부여의 향상
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                CSW를 사용하면서 특히 학습 습관 형성에 큰 효과를 보았습니다.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Impact 1 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Calendar className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">생활 습관 형성</h3>
                <p className="text-muted-foreground">
                  매일 일정한 시간에 학습을 시작하면서 일상 생활이 규칙적으로 자리 잡았습니다.
                </p>
              </Card>

              {/* Impact 2 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">상호 관찰을 통한 집중력 유지</h3>
                <p className="text-muted-foreground">
                  다른 이용자들과 함께 공부하며 긴장감과 집중력을 유지할 수 있었습니다.
                </p>
              </Card>

              {/* Impact 3 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Clock className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">학습 데이터 축적</h3>
                <p className="text-muted-foreground">
                  주기적으로 자신의 학습 데이터를 되돌아보며 목표 달성을 위한 계획을 세우기 쉬워졌습니다.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Features Grid Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted via-background to-background" />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4 animate-fade-in">
              <h2 className="font-display text-4xl sm:text-5xl font-bold">
                <span className="text-gradient">생산성</span>을 높이는 방법
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                완벽한 모드를 선택하고 잠재력을 최대한 발휘하세요
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Users, title: "그룹 모드", description: "전 세계의 동료들과 함께 공부하세요. 가상 룸에 참여하고 동기를 유지하세요.", gradient: "from-primary/20 to-primary/5", iconColor: "text-primary", href: "/group-study/" },
                { icon: Timer, title: "타이머 모드", description: "내장된 뽀모도로 타이머로 집중력을 높이세요. 똑똑하게 일하세요.", gradient: "from-secondary/20 to-secondary/5", iconColor: "text-secondary", href: "/study-timer/" },
                { icon: Youtube, title: "YT 모드", description: "일하면서 교육 콘텐츠를 스트리밍하세요. 지속적으로 배우고 성장하세요.", gradient: "from-accent/20 to-accent/5", iconColor: "text-accent", href: "/study-stream/" },
                { icon: User, title: "솔로 모드", description: "개인 작업 공간에서 집중하세요. 프라이버시와 생산성의 만남.", gradient: "from-muted-foreground/20 to-muted-foreground/5", iconColor: "text-foreground", href: "/online-workspace/" },
                { icon: LayoutGrid, title: "태스크", description: "칸반 보드로 작업을 정리하세요. 드래그, 드롭, 할 일 정복.", gradient: "from-primary/20 to-primary/5", iconColor: "text-primary", href: "/task-management/" },
                { icon: HelpCircle, title: "퀴즈", description: "대화형 퀴즈로 지식을 테스트하세요. 더 스마트하게 배우세요.", gradient: "from-secondary/20 to-secondary/5", iconColor: "text-secondary", href: "/quizzes/" },
                { icon: Layers, title: "플래시 카드", description: "디지털 플래시 카드로 모든 과목을 마스터하세요. 암기와 복습에 완벽합니다.", gradient: "from-accent/20 to-accent/5", iconColor: "text-accent", href: "/flash-cards/" },
                { icon: Bell, title: "알림", description: "마감일을 놓치지 마세요. 스마트 알림을 설정하여 학습을 정상 궤도에 올리세요.", gradient: "from-muted-foreground/20 to-muted-foreground/5", iconColor: "text-foreground", href: "/reminders/" },
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Link key={feature.title} to={feature.href} onClick={() => window.scrollTo(0, 0)}>
                    <Card
                      className="group relative overflow-hidden border-border bg-card hover:bg-card/80 p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl animate-fade-in h-full"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                      <div className="relative z-10 space-y-4">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className={`h-7 w-7 ${feature.iconColor}`} />
                        </div>
                        <h3 className="text-xl font-bold text-foreground">{feature.title}</h3>
                        <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                      </div>
                      <div className="absolute -bottom-2 -right-2 w-24 h-24 bg-primary/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Study Stream Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[100px] animate-glow-pulse" />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[100px] animate-glow-pulse" style={{ animationDelay: '1.5s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <Card className="border-2 border-border bg-card/50 backdrop-blur-xl overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-12 p-8 sm:p-12 lg:p-16">
                  <div className="space-y-8 animate-fade-in">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                      <Infinity className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold text-primary">제한 없음</span>
                    </div>
                    
                    <div className="space-y-4">
                      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                        스터디 <span className="text-gradient">스트림</span>
                      </h2>
                      <p className="text-2xl text-muted-foreground font-light">
                        생산성에 돈을 쓰지 마세요
                      </p>
                    </div>
                    
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      언제든지 무제한 스트림에 참여하세요. 가상 스터디룸에서 전 세계 수천 명의 학생들과 연결하세요. 집중하고, 동기를 유지하고, 생산적으로 - 완전 무료.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105" asChild>
                        <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">무료 스트림 시작</a>
                      </Button>
                      <Button size="lg" variant="outline" className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105" asChild>
                        <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">더 알아보기</a>
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                    <div className="space-y-4">
                      <h3 className="text-2xl font-bold text-foreground mb-6">
                        필요한 모든 것, <span className="text-gradient">영원히 무료</span>
                      </h3>
                      
                      <div className="space-y-3">
                        {["무제한 스터디 세션", "24시간 모든 룸 접근", "숨겨진 비용 없음", "모든 스트림에 즉시 참여", "글로벌 커뮤니티 연결", "프리미엄 기능 포함"].map((benefit, index) => (
                          <div key={benefit} className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors animate-slide-in" style={{ animationDelay: `${0.3 + index * 0.05}s` }}>
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                              <Check className="w-4 h-4 text-primary-foreground" />
                            </div>
                            <span className="text-foreground font-medium">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <Card className="p-6 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-3xl font-bold text-gradient mb-1">∞</p>
                          <p className="text-sm text-muted-foreground">이용 가능한 스트림</p>
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-gradient mb-1">$0</p>
                          <p className="text-sm text-muted-foreground">비용</p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/30 rounded-full blur-3xl animate-pulse" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <Card className="max-w-4xl mx-auto p-12 text-center bg-card/80 backdrop-blur-sm border-2">
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
                새로운 학습 경험을 시작하세요
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                온라인 자습실은 단순히 공부만 하는 공간이 아니라, 자신을 되돌아보고 학습 습관을 기르는 강력한 도구입니다. 한 번 경험해 보세요!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8">
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">무료로 시작하기</a>
                </Button>
                <Button asChild variant="outline" size="lg" className="font-semibold px-8">
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">더 알아보기</a>
                </Button>
              </div>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default IndexKr;
